import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useWebSocketContext } from 'src/state/webSocketState'

import { createThrottle } from './throttle'

// Apps with menu counters (first part of the url: /{app}/...)
export type CounterApp = 'expert' | 'sales'

// Module in the realtime event (key of /inspection/meta) -> app that shows its counters
const APP_BY_MODULE: Record<string, CounterApp> = {
    expert_trade_in: 'expert',
    sales_stock: 'sales',
    sales_process: 'sales',
    sales_physical_process: 'sales',
}

/**
 * Only when the tab is visible and the event is about the app on screen, the counters of the module on screen
 * are refreshed: the refresher of the app gets that module (f.ex. 'manager' in /expert/manager).
 */
const useCountersChanged = (refreshers: Record<CounterApp, (module: string) => Promise<unknown>>) => {
    const { subscribe, connected } = useWebSocketContext()
    const { pathname } = useLocation()

    const [, currentApp = '', currentModule = '', currentItemGroup = ''] = pathname.split('/')
    const currentRef = useRef({ app: currentApp, module: currentModule })
    currentRef.current = { app: currentApp, module: currentModule }

    const refreshersRef = useRef(refreshers)
    refreshersRef.current = refreshers

    const [throttle] = useState(() =>
        createThrottle(() => {
            const { app, module } = currentRef.current
            return refreshersRef.current[app as CounterApp]?.(module) ?? Promise.resolve()
        }),
    )

    useEffect(() => throttle.cancel, [])

    // Switched app/module/item group (f.ex. via the menu, sales processes -> publications): refresh the counters
    // Skip the initial mount, the counters for it are already loaded with the page.
    const isFirstMountRef = useRef(true)
    useEffect(() => {
        if (isFirstMountRef.current) {
            isFirstMountRef.current = false
            return
        }
        throttle.trigger()
    }, [currentApp, currentModule, currentItemGroup])

    // Back to the tab
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') throttle.trigger()
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => document.removeEventListener('visibilitychange', onVisible)
    }, [])

    // Reconnected (not the first connect: the counters are loaded with the page)
    const wasConnectedRef = useRef(false)
    useEffect(() => {
        if (!connected) return
        if (wasConnectedRef.current) throttle.trigger()
        wasConnectedRef.current = true
    }, [connected])

    useEffect(() => {
        if (!connected) return

        return subscribe('counters_changed', (data) => {
            if (document.visibilityState !== 'visible') return

            const modules: string[] = (data as { payload?: { modules?: string[] } })?.payload?.modules ?? []
            if (modules.some((module) => APP_BY_MODULE[module] === currentRef.current.app)) {
                throttle.trigger()
            }
        })
    }, [connected, subscribe])
}

export default useCountersChanged
